import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Droppable } from 'react-beautiful-dnd';
import PropTypes from 'prop-types';
import classNames from 'classNames';
import QuestionWorkspace from '~/components/quizzes/question-workspace';
import NewEntity from '~/components/common/new-entity';
import EmptyEntity from '~/components/common/empty-entity';

/**
 * Our canvas when building a quiz. Allows you to add questions by drag and drop.
 */
class QuizWorkspace extends Component {
	/**
	 * Constructor
	 *
	 * @param {Object} props
	 */
	constructor( props ) {
		super( props );

		// Calculate total points on this quiz
		let totalPoints = 0;
		this.props.workspace.present.questions.map(
			( question ) =>
				( totalPoints = parseInt( totalPoints + question.points, 10 ) )
		);

		/**
		 * Our state which contains:
		 * - isDropDisabled: whether the current droppable is enabled or not, depending which draggable is the current item.
		 * - serializedData: current data
		 * - savedSerializedData: last saved data
		 * - totalPoints: total points for this quiz
		 * - workspace: current workspace
		 */
		this.state = {
			isDropDisabled: true,
			serializedData: '',
			savedSerializedData: '',
			totalPoints: parseInt( totalPoints, 10 ),
			workspace: this.props.workspace.present,
		};

		this.maybeShowAlert = this.maybeShowAlert.bind( this );

		let saveHappened = false;
		let showingNotice = false;

		// Check if the user has hit the publish button in Gutenberg
		window.wp.data.subscribe( () => {
			if ( false === saveHappened ) {
				saveHappened =
					window.wp.data.select( 'core/editor' ) &&
					true ===
						window.wp.data.select( 'core/editor' ).isSavingPost();
			}
			if (
				saveHappened &&
				false ===
					window.wp.data.select( 'core/editor' ).isSavingPost() &&
				false === showingNotice
			) {
				saveHappened = false;
				showingNotice = true;
				// Save happened, update our state
				this.setState(
					{ savedSerializedData: this.state.serializedData },
					() => {
						showingNotice = false;
					}
				);
			}
		} );
	}

	/**
	 * Changes whether the droppable can be enabled/disabled. We cannot use a function for the isDropDisabled for Droppable
	 *
	 * @param {Object} props
	 * @param {Object} state
	 */
	static getDerivedStateFromProps( props, state ) {
		const workspace = props.workspace.present;

		if (
			workspace.currentDraggableEntity !== state.currentDraggableEntity &&
			workspace.currentDraggableEntity
		) {
			let isDropDisabled = true;

			// Only Draggable lessons can be dropped on that Droppable.
			if (
				'question' === workspace.currentDraggableEntity.type ||
				'sfwd-question' === workspace.currentDraggableEntity.type
			) {
				isDropDisabled = false;
			}

			return {
				isDropDisabled,
				workspace,
			};
		}

		return null;
	}

	/**
	 * Show alert if the user is trying to leave without saving
	 *
	 * @param {maybeShowAlert} e
	 */
	maybeShowAlert( e ) {
		// Classic editor
		if ( window.postL10n ) {
			if ( '' !== this.state.serializedData ) {
				return window.postL10n.saveAlert;
			}
		} else if (
			window.adminpage &&
			'sfwd-quiz_page_quizzes-builder' === window.adminpage
		) {
			// Standalone: if they're not clicking save, show alert
			if (
				'' !== this.state.serializedData &&
				'submit' !== e.target.activeElement.id
			) {
				e.returnValue = `${ LearnDashData.i18n.unsaved_changes }`;
				return e.returnValue;
			}
		} else {
			// Gutenberg
			// Our saved data is different from the current data, we need saving to avoid losing changes
			if (
				this.state.savedSerializedData !== this.state.serializedData
			) {
				e.returnValue = `${ LearnDashData.i18n.unsaved_changes }`;
				return e.returnValue;
			}
		}
	}

	/**
	 * Attach events once component is mounted
	 */
	componentDidMount() {
		// Classic editor
		if ( window.postL10n ) {
			window
				.jQuery( window )
				.on( 'beforeunload.edit-post', this.maybeShowAlert );
		} else {
			// Gutenberg
			window.addEventListener( 'beforeunload', this.maybeShowAlert );
		}
	}

	/**
	 * Detach events when component is unmounted
	 */
	componentWillUnmount() {
		// Classic editor
		if ( window.postL10n ) {
			window
				.jQuery( window )
				.off( 'beforeunload.edit-post', this.maybeShowAlert );
		} else {
			// Gutenberg
			window.removeEventListener( 'beforeunload', this.maybeShowAlert );
		}
	}

	/**
	 * When component is updated, serialize the data
	 *
	 * @param {Object} prevProps The previous props
	 */
	componentDidUpdate( prevProps ) {
		// For easier reading
		const workspace = this.props.workspace.present;
		const prevWorkspace = prevProps.workspace.present;

		// Serialized the workspace as soon as it's updated.
		if ( workspace !== prevWorkspace ) {
			// Recalculate total points
			let totalPoints = 0;
			workspace.questions.map(
				( question ) =>
					( totalPoints = parseInt(
						totalPoints + question.points,
						10
					) )
			);
			// Build serialized data for lessons
			const swfdData = workspace.questions.reduce(
				( accumulator, question ) => {
					const questionKey = `${ question.type }:${ question.ID }`;
					accumulator[ questionKey ] = {};
					return accumulator;
				},
				{}
			);

			this.setState( {
				serializedData: JSON.stringify( swfdData ),
				totalPoints: parseInt( totalPoints, 10 ),
			} );
		}
	}

	/**
	 * Render function
	 *
	 * @event maybeShowAlert
	 */
	render() {
		const { isDropDisabled, totalPoints } = this.state;
		const { workspace, totalLessons } = this.props;
		return (
			<div className="ld__builder--content">
				{ /* Our main droppable where questions can be dropped and reordered */ }
				<Droppable
					droppableId={ JSON.stringify( {
						ID: 'quiz-workspace-droppable',
						type: 'quiz',
					} ) }
					isDropDisabled={ isDropDisabled }
				>
					{ ( provided, snapshot ) => (
						<div
							ref={ provided.innerRef }
							className={ classNames( {
								'-is-dragging-over': snapshot.isDraggingOver,
							} ) }
						>
							{ /* Loop through all the questions in our store */ }
							{ workspace.present.questions.map(
								( question, index ) => (
									<QuestionWorkspace
										key={ question.ID }
										question={ question }
										index={ index }
										provided={ provided }
									/>
								)
							) }
							{ /* Placeholder for dropping new questions */ }
							<ul
								className={ classNames( {
									'ld__builder--placeholder': true,
									'-question': true,
									'-is-empty': 0 === this.props.totalLessons,
								} ) }
								data-label={ `${ LearnDashData.i18n.drop_question }` }
							>
								{ provided.placeholder }

								{ 0 === totalLessons && (
									<li>
										<EmptyEntity
											type="quiz"
											content="question"
										/>
									</li>
								) }
							</ul>
						</div>
					) }
				</Droppable>

				<div className="ld__builder--quiz-footer">
					<div className="ld__builder--new-entities">
						<NewEntity type="question" />
					</div>
					<div className="ld__builder--quiz-total">
						{ `${ LearnDashData.i18n.total_points } ${ totalPoints }` }{ ' ' }
						{ 1 < totalPoints
							? `${ LearnDashData.labels.points.plural }`
							: `${ LearnDashData.labels.points.singular }` }
					</div>
				</div>

				{ /* Maintain backwards compatibility to save data */ }
				<input
					type="hidden"
					id="learndash_builder_data"
					name={ `learndash_builder[sfwd-quiz][${ LearnDashData.post_data.builder_post_id }]` }
					value={ this.state.serializedData }
				/>
			</div>
		);
	}
}

/**
 * Valid props
 */
QuizWorkspace.propTypes = {
	workspace: PropTypes.shape( {
		present: PropTypes.object,
	} ),
	data: PropTypes.shape( {
		labels: PropTypes.object,
	} ),
	totalLessons: PropTypes.number,
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = ( state ) => {
	return {
		workspace: state.workspace,
		data: state.data,
	};
};

/**
 *
 */
const mapDispatchToProps = () => ( {} );

export default connect( mapStateToProps, mapDispatchToProps )( QuizWorkspace );
